import ReactDatePicker, { CalendarContainer, registerLocale } from 'react-datepicker';
import type { Placement } from '@floating-ui/react';
import { Translate } from 'react-i18nify';
import sv from 'date-fns/locale/sv';
import moment from 'moment';
import { useRef, useMemo, useCallback } from 'react';
import styles from './DatePicker.module.scss';
import Button from '../../Button';
registerLocale('sv', sv as unknown as Parameters<typeof registerLocale>[1]);

type Props = {
  selectedDate?: Date;
  onSelectDate(date: Date): void;
  locale?: string;
  disabled?: boolean;
  popperPlacement?: Placement;
  minDate?: Date;
  maxDate?: Date;
  suggestedDates?: SuggestedDate[];
  inline?: boolean;
  selectsStart?: boolean;
  selectsEnd?: boolean;
  startDate?: Date;
  endDate?: Date;
  excludeDates?: Date[];
  showDropdowns?: boolean;
};

export default function DatePicker({
  selectedDate,
  onSelectDate,
  locale = 'sv',
  popperPlacement = 'bottom',
  minDate = moment().toDate(),
  suggestedDates,
  showDropdowns = false,
  ...delegated
}: Props) {
  const datePickerRef = useRef<ReactDatePicker>(null);
  const lastSelectedRef = useRef<Date | undefined>(selectedDate);
  const savedByClickRef = useRef(false);

  const handleChange = useCallback(
    (date: Date | null) => {
      if (date) {
        lastSelectedRef.current = date;
        savedByClickRef.current = true;
        onSelectDate(date);
      }
    },
    [onSelectDate]
  );

  const handleMonthYearChange = useCallback((date: Date) => {
    lastSelectedRef.current = date;
    savedByClickRef.current = false;
  }, []);

  const handleCalendarClose = useCallback(() => {
    if (!savedByClickRef.current && lastSelectedRef.current) {
      onSelectDate(lastSelectedRef.current);
    }
    savedByClickRef.current = false;
  }, [onSelectDate]);

  const CustomCalendarContainer = useMemo(() => {
    return ({ className, children }: { className?: string; children: React.ReactNode }) => (
      <div className={styles.calendarWrapper}>
        <CalendarContainer className={className}>
          <div className="pos-rel">{children}</div>
        </CalendarContainer>

        {suggestedDates?.length ? (
          <div className={styles.suggestedDatesContainer}>
            <p className={styles.suggestedDatesLabel}>Snabbval:</p>
            <div className={styles.suggestedDatesButtons}>
              {suggestedDates.map((suggestion) => (
                <Button
                  key={suggestion.date}
                  type="button"
                  variant="secondary"
                  size="small"
                  onClick={() => {
                    const date = moment(suggestion.date).toDate();
                    lastSelectedRef.current = date;
                    savedByClickRef.current = true;
                    onSelectDate(date);
                    datePickerRef.current?.setOpen(false);
                  }}
                >
                  {suggestion.title}
                </Button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  }, [suggestedDates, onSelectDate]);

  return (
    <div className={delegated.disabled ? styles.disabled : undefined}>
      <ReactDatePicker
        ref={datePickerRef}
        customInput={<CustomDateInput />}
        minDate={minDate}
        selected={selectedDate}
        onChange={handleChange}
        onMonthChange={handleMonthYearChange}
        onYearChange={handleMonthYearChange}
        onCalendarClose={handleCalendarClose}
        locale={locale}
        dateFormat="yyyy-MM-dd"
        popperPlacement={popperPlacement}
        calendarContainer={CustomCalendarContainer}
        showYearDropdown={showDropdowns}
        showMonthDropdown={showDropdowns}
        dropdownMode={showDropdowns ? 'select' : undefined}
        {...delegated}
      />
    </div>
  );
}

const CustomDateInput = ({ value, onClick }: { value?: string; onClick?: () => void }) => {
  return (
    <div className={styles.wrapper}>
      <div onClick={onClick}>
        {!value ? <Translate value="decision_support_view.checkup_section.choose_date" /> : <span>{value}</span>}
      </div>
    </div>
  );
};
